'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { ChatHeader } from './ChatHeader';
import { ChatList } from './ChatList';
import { ChatMessages } from './ChatMessages';
import { cn } from '@/src/shared/lib/utils';
import { useChatStore } from '../model/useChatStore';
import { useAuthStore } from '@/src/shared/lib/store/auth-store';
import { useModal } from '@/src/shared/hooks/useModal';
import type { ChatFilter } from '../types';

interface ChatWidgetProps {
  className?: string;
  initialConversationId?: string; // Auto-select this conversation
  targetActorId?: string; // Create or find conversation with this actor
}

export const ChatWidget = ({
  className,
  initialConversationId,
  targetActorId,
}: ChatWidgetProps) => {
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const [userDismissed, setUserDismissed] = useState(false);
  const [filter, setFilter] = useState<ChatFilter>('All');
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const searchParams = useSearchParams();

  // Check if user is authenticated and get active actor
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const activeActorId = useAuthStore((state) => state.activeActor?.id);
  const activeActor = useAuthStore((state) => state.activeActor);

  // Modal for creating orders
  const { open: openPostTaskModal } = useModal('post-task');

  const {
    conversations,
    loadConversations,
    refreshConversations,
    connectSocket,
    disconnectSocket,
    connectionStatus,
    isLoadingConversations,
    error,
    getOrCreateConversation,
    reset,
  } = useChatStore();

  // Initialize on mount and when active actor changes
  useEffect(() => {
    if (!isAuthenticated) {
      console.log('[ChatWidget] Not authenticated, skipping initialization');
      return;
    }

    console.log('[ChatWidget] Initializing chat for actor:', activeActorId);

    // Reset chat state to clear old data from previous actor
    reset();

    // Connect to WebSocket (uses HTTP-only cookies, no token needed)
    // connectSocket will handle disconnecting existing connection if any
    connectSocket();

    // Load conversations
    loadConversations();

    // Cleanup on unmount or actor change
    return () => {
      disconnectSocket();
    };
  }, [isAuthenticated, activeActorId, connectSocket, disconnectSocket, loadConversations, reset]);

  // Reset selected conversation when actor changes
  useEffect(() => {
    setSelectedConversationId(null);
  }, [activeActorId]);

  // Reset userDismissed when URL params or initialConversationId change (new navigation intent)
  useEffect(() => {
    setUserDismissed(false);
  }, [searchParams, initialConversationId]);

  // Auto-select conversation from URL params or prop
  useEffect(() => {
    const conversationIdFromUrl = searchParams.get('conversationId');
    const targetConversationId = conversationIdFromUrl || initialConversationId;

    if (targetConversationId && !selectedConversationId && !userDismissed) {
      setSelectedConversationId(targetConversationId);
    }
  }, [searchParams, initialConversationId, selectedConversationId, userDismissed]);

  // Handle targetActorId - create or find conversation with this actor
  useEffect(() => {
    const userIdFromUrl = searchParams.get('user');
    const targetId = userIdFromUrl || targetActorId;

    if (!targetId || !isAuthenticated || isCreatingConversation) {
      return;
    }

    // Don't create if we already selected a conversation
    if (selectedConversationId) {
      return;
    }

    const initConversation = async () => {
      setIsCreatingConversation(true);
      try {
        const conversationId = await getOrCreateConversation(targetId);
        setSelectedConversationId(conversationId);
      } catch (err) {
        console.error('Failed to create conversation:', err);
      } finally {
        setIsCreatingConversation(false);
      }
    };

    initConversation();
  }, [searchParams, targetActorId, isAuthenticated, isCreatingConversation, selectedConversationId, getOrCreateConversation]);

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-text-secondary">
          Please log in to view your conversations
        </div>
      </div>
    );
  }

  // Handle back from chat messages (deselect conversation)
  const handleBackFromMessages = () => {
    setUserDismissed(true);
    setSelectedConversationId(null);
  };

  // Handle create order button click
  const handleCreateOrder = () => {
    if (!selectedConversationId || !activeActor) return;

    // Find selected conversation
    const conversation = conversations.find((c) => c.id === selectedConversationId);
    if (!conversation) return;

    // Get target actor (the other participant)
    const targetActor = conversation.otherParticipant;
    if (!targetActor) return;

    // Open modal with direct order parameters
    openPostTaskModal({
      orderType: 'DIRECT',
      targetActorId: targetActor.id,
      targetActorName: targetActor.displayName || targetActor.name || 'User',
      currentActorType: activeActor.type,
      onSuccess: async (orderId: string) => {
        console.log('[ChatWidget] Order created successfully:', orderId);
        // Refresh conversations to get the updated activeOrder
        await refreshConversations();
      },
    });
  };

  return (
    <div
      className={cn(
        'flex flex-col h-full w-full overflow-hidden',
        'bg-background-primary',
        className
      )}
    >
      {/* Unified Header */}
      <ChatHeader
        title=""
        filterValue={filter}
        showFilter={true}
        onFilterChange={setFilter}
        connectionStatus={connectionStatus}
      />

      {/* Separator line */}
      <div className="w-full h-px bg-accent-secondary" />

      {/* Error */}
      {error && (
        <div className="px-5 py-2 bg-red-100 text-red-800 text-sm">{error}</div>
      )}

      {/* Loading */}
      {(isLoadingConversations || isCreatingConversation) && (
        <div className="flex items-center justify-center p-8">
          <div className="text-text-secondary">
            {isCreatingConversation ? 'Starting conversation...' : 'Loading conversations...'}
          </div>
        </div>
      )}

      {/* Content: Conversation List + Messages */}
      {!isLoadingConversations && !isCreatingConversation && (
        <div className="flex flex-1 overflow-hidden">
          {/* Left side - Conversation List */}
          <div className="w-96 flex-shrink-0 overflow-y-auto">
            <ChatList
              selectedConversationId={selectedConversationId}
              onSelectConversation={setSelectedConversationId}
              filter={filter}
            />
          </div>

          {/* Right side - Chat Messages */}
          <div className="flex-1 border-l border-accent-secondary">
            <ChatMessages
              conversationId={selectedConversationId}
              onBack={selectedConversationId ? handleBackFromMessages : undefined}
            />
          </div>
        </div>
      )}
    </div>
  );
};
